import { useEffect } from 'react'
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRoute,
  useRouterState,
} from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Provider as JotaiProvider } from 'jotai'

import { Toaster } from 'sonner'
import { AppErrorPage, AppNotFoundPage } from '@/features/shared/components'
import { ThemeProvider } from '@/lib/theme-provider'
import {
  generateGlobalLinks,
  generateMetaTags,
  notFoundSEO,
} from '@/lib/seo'
import {
  getOrganizationSchema,
  getWebSiteSchema,
  renderJsonLd,
} from '@/lib/structured-data'
import { ColorSystemPicker } from '@/components/ColorSystemPicker'
import { TourProvider } from '@/features/tours/core/TourProvider'
import { CampaignBanner } from '@/features/campaign-banner/components/CampaignBanner'

// Import CSS - Vite will automatically inject the hashed CSS link during SSR
import '@fontsource-variable/geist'
import '@fontsource-variable/geist-mono'
import '../styles.css'

export const Route = createRootRoute({
  // An unmatched URL renders notFoundComponent with no child route to supply
  // its own head, so without this the 404 inherits the homepage's defaults.
  head: ({ matches }) => ({
    meta: generateMetaTags(
      matches.some((m) => m.globalNotFound) ? notFoundSEO : undefined,
    ),
    links: generateGlobalLinks(),
  }),

  component: RootComponent,
  errorComponent: AppErrorPage,
  notFoundComponent: AppNotFoundPage,
})

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
    },
  },
})

// gtag('config', ...) in <head> only runs once, on the initial hard load —
// it never fires again on TanStack Router's client-side navigations. This
// fires the page_view GA4 would otherwise have sent automatically (disabled
// via send_page_view: false above), on first mount and on every route change.
function GoogleAnalyticsPageviewTracker() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname + state.location.search,
  })

  useEffect(() => {
    const gtag = (window as any).gtag
    if (typeof gtag !== 'function') return
    gtag('event', 'page_view', {
      page_path: pathname,
      page_title: document.title,
    })
  }, [pathname])

  return null
}

function RootComponent() {
  // Organization + WebSite JSON-LD are rendered here (sitewide) only — do not
  // also render them per-page, or the same schema type ends up duplicated.
  const organizationSchema = getOrganizationSchema()
  const websiteSchema = getWebSiteSchema()

  return (
    <html lang="en" className="dark" suppressHydrationWarning>
      <head>
        <meta name="theme-color" content="#0a0a0a" />
        {/* Google Search Console Verification */}
        <meta
          name="google-site-verification"
          content={
            import.meta.env.VITE_GSC_VERIFICATION_CODE ||
            'QnEmxdt6pXoPlJCvkz0B75FKxpU8jPaK1Buil2Z9wOM'
          }
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var t=localStorage.getItem('modelstack-theme');if(t==='light'||t==='dark')document.documentElement.className=t}catch(e){}})()`,
          }}
        />
        <HeadContent />
        {/* Structured Data (JSON-LD) */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: renderJsonLd(organizationSchema),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: renderJsonLd(websiteSchema),
          }}
        />
        {/* Google Analytics */}
        <script
          defer
          src={`https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GA_TRACKING_ID || 'G-FVMG7RNT53'}`}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${import.meta.env.VITE_GA_TRACKING_ID || 'G-FVMG7RNT53'}', { send_page_view: false });`,
          }}
        />
      </head>
      <body className="min-h-screen antialiased">
        <ThemeProvider defaultTheme="dark">
          <QueryClientProvider client={queryClient}>
            <JotaiProvider>
              <TourProvider>
                <GoogleAnalyticsPageviewTracker />
                <CampaignBanner />
                <Outlet />
                <Toaster position="top-center" richColors />
                <ColorSystemPicker />
              </TourProvider>
            </JotaiProvider>
          </QueryClientProvider>
        </ThemeProvider>
        <Scripts />
      </body>
    </html>
  )
}
